import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useGetGridMetadataQuery } from '../menu/api/menuApi';
import {
  useCalculateSalaryMutation,
  useGetGridDataQuery,
  useGetRelationCommandMetadataQuery,
} from '../grid/api/gridApi';
import type { ColumnMetadata, GridFilter } from '../grid/types';
import type { RelationPickerTarget } from '../grid/components/dialogs/RelationPickerDialog';
import type { Command } from '../menu/types';
import { normalizeGridKey, parseColumnMetadata } from '../grid/utils';
import { extractErrorMessage } from '../../utils/errorUtils';

const EMPTY_ROWS: Record<string, unknown>[] = [];

const getSourceId = (row: Record<string, unknown>, field: string) =>
  String(row[field] ?? row.Id ?? '');

const getDisplayValue = (row: Record<string, unknown>, column: ColumnMetadata) => {
  const field = column.field || column.dataField || '';
  return String(row[field] ?? '');
};

const isRelativeSameOriginPath = (value: string): boolean => {
  if (!value.startsWith('/') || value.startsWith('//')) return false;

  try {
    return new URL(value, window.location.origin).origin === window.location.origin;
  } catch {
    return false;
  }
};

type SalaryCalculateDialogProps = {
  command: Command | null;
  parentId?: string;
  relationTargets: Record<string, RelationPickerTarget>;
  onClose: () => void;
  onError: (message: string) => void;
};

export const SalaryCalculateDialog = ({
  command,
  parentId,
  relationTargets,
  onClose,
  onError,
}: SalaryCalculateDialogProps) => {
  const open = Boolean(command);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [calculateSalary, { isLoading: isCalculating }] = useCalculateSalaryMutation();
  const metadataQuery = useGetRelationCommandMetadataQuery(
    { commandKey: command?.key ?? '' },
    { skip: !open || !command?.key }
  );
  const metadata = metadataQuery.data;

  const sourceMetadataQuery = useGetGridMetadataQuery(
    { nodeKey: metadata?.relatedGridKey ?? '' },
    { skip: !open || !metadata?.relatedGridKey }
  );
  const sourceMetadata = sourceMetadataQuery.data;
  const sourceColumns = useMemo<ColumnMetadata[]>(
    () => parseColumnMetadata(sourceMetadata?.columns),
    [sourceMetadata?.columns]
  );
  const visibleColumns = useMemo(
    () => sourceColumns.filter((column) => column.visible !== false),
    [sourceColumns]
  );
  const sourceDataQuery = useGetGridDataQuery(
    useMemo(() => {
      const pageSize = sourceMetadata?.numberOfRowsPerPage ?? 0;
      const useServerPaging = sourceMetadata?.usePaging === true && pageSize > 0;
      const term = searchTerm.trim();
      const fields = sourceColumns
        .map((column) => column.field || column.dataField)
        .filter((field): field is string => Boolean(field));
      const filter: unknown[] = [];
      fields.forEach((field, index) => {
        if (index > 0) filter.push('or');
        filter.push([field, 'contains', term]);
      });

      return {
        options: {
          nodeKey: metadata?.relatedGridKey ?? '',
          ...(useServerPaging ? { skip: page * pageSize, take: pageSize } : {}),
          ...(useServerPaging && term && fields.length ? { filter: filter as GridFilter } : {}),
        },
        columnMetadata: sourceColumns,
      };
    }, [
      metadata?.relatedGridKey,
      page,
      searchTerm,
      sourceColumns,
      sourceMetadata?.numberOfRowsPerPage,
      sourceMetadata?.usePaging,
    ]),
    { skip: !open || !metadata?.relatedGridKey || !sourceMetadata }
  );

  const targetKey = normalizeGridKey(metadata?.relatedTargetGridKey);
  const target = targetKey
    ? Object.values(relationTargets).find((item) => normalizeGridKey(item.gridKey) === targetKey)
    : undefined;
  const targetRows = target?.targetRows ?? EMPTY_ROWS;
  const sourceKeyField = metadata?.sourceKeyField || 'Id';
  const targetField = metadata?.targetGridField || '';

  const existingIds = useMemo(() => {
    const ids = new Set<string>();
    targetRows.forEach((row) => {
      const value = row[targetField];
      if (value !== undefined && value !== null && String(value).trim()) {
        ids.add(String(value));
      }
    });
    return ids;
  }, [targetField, targetRows]);

  const sourceRows = sourceDataQuery.data?.data ?? EMPTY_ROWS;
  const pageSize = sourceMetadata?.numberOfRowsPerPage ?? 0;
  const useServerPaging = sourceMetadata?.usePaging === true && pageSize > 0;
  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (useServerPaging || !term) return sourceRows;
    return sourceRows.filter((row) =>
      sourceColumns.some((column) => getDisplayValue(row, column).toLowerCase().includes(term))
    );
  }, [searchTerm, sourceColumns, sourceRows, useServerPaging]);

  const visibleRows = useServerPaging ? sourceRows : filteredRows;
  const totalCount = useServerPaging
    ? (sourceDataQuery.data?.totalCount ?? sourceRows.length)
    : filteredRows.length;
  const totalPages = useServerPaging ? Math.max(1, Math.ceil(totalCount / pageSize)) : 1;
  const selectableIds = visibleRows
    .map((row) => getSourceId(row, sourceKeyField))
    .filter((id) => id && !existingIds.has(id));
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));

  const toggleId = (id: string) => {
    if (!id || existingIds.has(id)) return;
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(0);
  };

  const handleConfirm = async () => {
    const numericParentId = Number(parentId);
    if (!metadata || !target || !Number.isInteger(numericParentId) || numericParentId <= 0) {
      onError('Save salary calculation first.');
      return;
    }

    const targetGridId = metadata.relatedTargetGridId;
    const calculateUrl = metadata.calculateUrl;
    if (
      !calculateUrl ||
      !isRelativeSameOriginPath(calculateUrl) ||
      typeof targetGridId !== 'number' ||
      !Number.isInteger(targetGridId)
    ) {
      onError('Salary Calculate command is not configured correctly.');
      return;
    }

    const selectedKeys = Array.from(selectedIds)
      .filter((id) => !existingIds.has(id))
      .map(Number)
      .filter((id) => Number.isInteger(id) && id > 0);

    if (selectedKeys.length === 0) return;

    try {
      const rows = await calculateSalary({
        url: calculateUrl,
        request: {
          NodeKey: targetGridId,
          ParentId: numericParentId,
          SelectedKeys: selectedKeys,
        },
      }).unwrap();
      target.onAddRowWithData(rows);
      onClose();
    } catch (error) {
      onError(extractErrorMessage(error, 'Failed to calculate salary.'));
    }
  };

  const isLoading =
    metadataQuery.isLoading ||
    sourceMetadataQuery.isLoading ||
    sourceDataQuery.isLoading ||
    isCalculating;
  const error = metadataQuery.error || sourceMetadataQuery.error || sourceDataQuery.error;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>{metadata?.popupCaption || metadata?.caption || 'Add employees'}</DialogTitle>
      <DialogContent dividers>
        {metadata?.popupText && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {metadata.popupText}
          </Typography>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {extractErrorMessage(error, 'Failed to load employees.')}
          </Alert>
        )}
        {!target && metadata && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Add Employees target grid is not available on this detail form.
          </Alert>
        )}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : !error && target ? (
          <>
            <TextField
              fullWidth
              size="small"
              label="Search"
              value={searchTerm}
              onChange={(event) => handleSearchChange(event.target.value)}
              sx={{ mb: 2 }}
            />
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allSelected}
                      disabled={selectableIds.length === 0}
                      onChange={() =>
                        setSelectedIds((current) => {
                          const next = new Set(current);
                          selectableIds.forEach((id) =>
                            allSelected ? next.delete(id) : next.add(id)
                          );
                          return next;
                        })
                      }
                    />
                  </TableCell>
                  {visibleColumns.map((column) => (
                    <TableCell key={column.field || column.dataField}>
                      {column.caption || column.title || column.field || column.dataField}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length + 1} align="center">
                      No employees found.
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleRows.map((row) => {
                    const id = getSourceId(row, sourceKeyField);
                    const alreadyAdded = existingIds.has(id);
                    return (
                      <TableRow key={id} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedIds.has(id)}
                            disabled={alreadyAdded}
                            onChange={() => toggleId(id)}
                          />
                        </TableCell>
                        {visibleColumns.map((column) => (
                          <TableCell key={column.field || column.dataField}>
                            {getDisplayValue(row, column)}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
            {useServerPaging && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mt: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Page {totalCount === 0 ? 0 : page + 1} of {totalPages} ({totalCount} items)
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    onClick={() => setPage((current) => Math.max(0, current - 1))}
                    disabled={page === 0}
                  >
                    Previous
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setPage((current) => current + 1)}
                    disabled={(page + 1) * pageSize >= totalCount}
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            )}
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isLoading || Boolean(error) || !target || selectedIds.size === 0}
        >
          Add employees
        </Button>
      </DialogActions>
    </Dialog>
  );
};
